/**
 * 西暦から和暦に変換するユーティリティ関数
 */

// 元号の定義
export type JapaneseEra = {
  name: string;
  nameShort: string;
  startDate: Date;
  endDate: Date | null;
};

export const JAPANESE_ERAS: JapaneseEra[] = [
  {
    name: '令和',
    nameShort: 'R',
    startDate: new Date('2019-05-01'),
    endDate: null,
  },
  {
    name: '平成',
    nameShort: 'H',
    startDate: new Date('1989-01-08'),
    endDate: new Date('2019-04-30'),
  },
  {
    name: '昭和',
    nameShort: 'S',
    startDate: new Date('1926-12-25'),
    endDate: new Date('1989-01-07'),
  },
  {
    name: '大正',
    nameShort: 'T',
    startDate: new Date('1912-07-30'),
    endDate: new Date('1926-12-24'),
  },
  {
    name: '明治',
    nameShort: 'M',
    startDate: new Date('1868-01-25'),
    endDate: new Date('1912-07-29'),
  },
];

/**
 * 西暦日付を和暦に変換する
 * @param dateStr 西暦日付（YYYY-MM-DD形式）
 * @returns 和暦日付（例：令和5年5月1日）
 */
export const toJapaneseDate = (dateStr: string): string => {
  if (!dateStr) return '';

  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';

    // 対応する元号を検索
    const era = JAPANESE_ERAS.find(
      (era) =>
        date >= era.startDate && (era.endDate === null || date <= era.endDate)
    );

    if (!era) return dateStr; // 対応する元号が見つからない場合は元の日付を返す

    // 元号での年を計算
    const yearInEra = date.getFullYear() - era.startDate.getFullYear() + 1;
    const month = date.getMonth() + 1;
    const day = date.getDate();

    // 元号1年の場合は「元年」と表示
    const yearString = yearInEra === 1 ? '元' : yearInEra.toString();

    return `${era.name}${yearString}年${month}月${day}日`;
  } catch (error) {
    console.error('Date conversion error:', error);
    return '';
  }
};

/**
 * 和暦日付を西暦に変換する
 * @param japaneseDateStr 和暦日付（例：令和5年5月1日、R5.5.1、令和元年5月1日）
 * @returns 西暦日付（YYYY-MM-DD形式）
 */
export const fromJapaneseDate = (japaneseDateStr: string): string | null => {
  if (!japaneseDateStr) return null;

  try {
    // 正規表現で和暦をパース
    // 例：令和5年5月1日、R5.5.1、令和元年5月1日
    const fullPattern = /^(令和|平成|昭和|大正|明治)(元|\d+)年(\d+)月(\d+)日$/;
    const shortPattern = /^([RSHMT])(\d+)[\.\/](\d+)[\.\/](\d+)$/;
    const westernPattern = /^(\d{4})-(\d{1,2})-(\d{1,2})$/;

    let era: JapaneseEra | undefined;
    let yearInEra: number;
    let month: number;
    let day: number;

    const fullMatch = fullPattern.exec(japaneseDateStr);
    const shortMatch = shortPattern.exec(japaneseDateStr);
    const westernMatch = westernPattern.exec(japaneseDateStr);

    if (fullMatch) {
      const [, eraName, yearStr, monthStr, dayStr] = fullMatch;
      era = JAPANESE_ERAS.find((e) => e.name === eraName);
      yearInEra = yearStr === '元' ? 1 : parseInt(yearStr, 10);
      month = parseInt(monthStr, 10);
      day = parseInt(dayStr, 10);
    } else if (shortMatch) {
      const [, eraShort, yearStr, monthStr, dayStr] = shortMatch;
      era = JAPANESE_ERAS.find((e) => e.nameShort === eraShort);
      yearInEra = parseInt(yearStr, 10);
      month = parseInt(monthStr, 10);
      day = parseInt(dayStr, 10);
    } else if (westernMatch) {
      // 西暦形式の場合はそのまま返す
      const [, year, monthStr, dayStr] = westernMatch;
      return `${year}-${monthStr.padStart(2, '0')}-${dayStr.padStart(2, '0')}`;
    } else {
      return null; // パースできない場合
    }

    if (!era) return null;

    // 西暦年を計算
    const year = era.startDate.getFullYear() + yearInEra - 1;

    // 日付の妥当性チェック
    const date = new Date(year, month - 1, day);
    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day
    ) {
      return null; // 無効な日付
    }

    // YYYY-MM-DD形式に変換
    return `${year}-${month.toString().padStart(2, '0')}-${day
      .toString()
      .padStart(2, '0')}`;
  } catch (error) {
    console.error('Date conversion error:', error);
    return null;
  }
};

/**
 * 西暦年から和暦年を取得する
 * @param year 西暦年
 * @returns 和暦年（例：令和5年、昭和64年/平成元年）
 */
export const getJapaneseEraYear = (year: number): string => {
  // 和暦が複数ある西暦年の特別処理
  if (year === 1989) {
    return '昭和64年/平成元年';
  } else if (year === 1926) {
    return '大正15年/昭和元年';
  } else if (year === 1912) {
    return '明治45年/大正元年';
  }

  // 各元号の期間内かチェック
  const date = new Date(year, 0, 1); // その年の1月1日
  const era = JAPANESE_ERAS.find(
    (era) =>
      date >= era.startDate && (era.endDate === null || date <= era.endDate)
  );

  if (!era) return ''; // 対応する元号が見つからない場合は空文字を返す

  // 元号での年を計算
  const yearInEra = year - era.startDate.getFullYear() + 1;
  // 元号1年の場合は「元年」と表示
  const yearString = yearInEra === 1 ? '元年' : `${yearInEra}年`;

  return `${era.name}${yearString}`;
};
